/**
 * Plans v2 card 2.4: the signed plan claim, webview side.
 *
 * `GET /me/plan-claim` returns a token signed by the server (14 days). The
 * webview only carries it: Rust verifies it against the public keys built into
 * the app, checks it is for THIS account and not expired, stores it
 * (`plan_claim_store`) and hands back the verified claim. On a later start,
 * online or not, `plan_claim_load` re-verifies the stored one. Only a claim
 * that came back from Rust feeds the entitlements store, as its most trusted
 * source.
 *
 * Offline (owner, 16.10.2026): the app keeps working for the claim's 14 days.
 * Past its expiry, while the server cannot be reached, the app shows only the
 * sign-in page (platform/desktopGate.js), using the note kept below. A plain
 * "Sign out" keeps the stored claim; "Remove from device" (and deleting the
 * account) forgets it (`forgetPlanClaim`). Rust refuses a stored claim for
 * any other account, so the next account's sign-in simply replaces it.
 *
 * Desktop only; a web build, a test or a recorded demo run (synthetic) has no
 * claim and falls back to /auth/usage.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/isTauri';
// A demo/e2e BUILD only (platform/gateExempt.js): a release build always
// checks the claim, so a runtime flag cannot switch off the offline expiry.
import { isGateExemptRun } from '../platform/gateExempt';
import { useEntitlementsStore } from '../store/entitlementsStore';

const supported = () => isTauri() && !isGateExemptRun();

const always = () => true;

/**
 * Which account the stored claim belongs to and when it expires (unix
 * seconds), so the app can tell "offline past the claim" from "never had a
 * claim" without asking Rust on every check. Not a secret and trusted for
 * nothing else: Rust re-verifies the token itself. Device-global (`apiboo.`
 * prefix), so a plain sign-out keeps it with the claim.
 */
export const LAST_CLAIM_KEY = 'apiboo.planClaim.last';

/** `{ userId, exp }` of the last claim Rust verified, or null. */
export function lastPlanClaim() {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_CLAIM_KEY) || 'null');
    if (!v || v.userId == null || !Number.isFinite(v.exp)) return null;
    return { userId: String(v.userId), exp: v.exp };
  } catch {
    return null;
  }
}

function remember(claim, userId) {
  try {
    const exp = Number(claim.exp);
    if (!Number.isFinite(exp)) return;
    localStorage.setItem(LAST_CLAIM_KEY, JSON.stringify({ userId: String(claim.sub ?? userId), exp }));
  } catch {
    // best effort: only the offline-expiry gate and its warning read it
  }
}

/**
 * True when `userId`'s last verified claim has run out (`now` in ms): the
 * 14-day offline grace is over. False when there never was a claim.
 */
export function planClaimExpired(userId, now = Date.now()) {
  const last = lastPlanClaim();
  if (!last || userId == null || last.userId !== String(userId)) return false;
  return last.exp * 1000 <= now;
}

function apply(claim, isCurrent, userId) {
  // The caller's guard (entitlementsFeed): a claim that arrives after a
  // sign-out, an account switch or a newer refresh is dropped.
  if (!isCurrent()) return false;
  if (!claim || typeof claim !== 'object' || !claim.plan) return false;
  remember(claim, userId);
  useEntitlementsStore.getState().setSnapshot({
    plan: claim.plan,
    limits: claim.limits,
    capabilities: claim.caps,
    source: 'claim',
  });
  return true;
}

/** The stored claim for `userId`, verified again by Rust. Works offline. */
export async function loadCachedPlanClaim(userId, isCurrent = always) {
  if (!supported() || userId == null) return false;
  try {
    return apply(await invoke('plan_claim_load', { userId: String(userId) }), isCurrent, userId);
  } catch {
    return false;
  }
}

/**
 * A fresh claim: `fetchClaim()` is the caller's authenticated
 * `GET /me/plan-claim` (→ `{ token, exp, plan }`). A 503 (server without a
 * signing key), offline or a token Rust refuses all leave the store as it is.
 */
export async function refreshPlanClaim(userId, fetchClaim, isCurrent = always) {
  if (!supported() || userId == null || typeof fetchClaim !== 'function') return false;
  try {
    const res = await fetchClaim();
    const token = res?.token;
    if (typeof token !== 'string' || !token) return false;
    // Signed out (or switched account) while the fetch was in flight: do not
    // store the outgoing account's claim after the session ended.
    if (!isCurrent()) return false;
    return apply(await invoke('plan_claim_store', { token, userId: String(userId) }), isCurrent, userId);
  } catch {
    return false;
  }
}

/**
 * "Remove from device" / account deletion: drop the stored claim and its note.
 * A plain sign-out keeps both (owner, 16.10.2026).
 */
export async function forgetPlanClaim() {
  try {
    localStorage.removeItem(LAST_CLAIM_KEY);
  } catch {
    // ignore
  }
  if (!supported()) return;
  try {
    await invoke('plan_claim_forget');
  } catch {
    // best effort
  }
}
